import { Component, EventEmitter, Input, Output, OnInit, HostListener, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NewPatientInfo } from '../../card/new-patient-card/new-patient-card.component';

/**
 * 患者詳細インターフェース（APIドキュメントに基づく）
 */
export interface PatientDetailInfo {
  patientId: number;
  patientName: string;
  age: number;
  gender: string; // '男' または '女'
  ward?: string;
  floor?: string;
  room: string;
  bed: string;
  assessment?: {
    delirium: string; // 'あり' または 'なし'
    fallRisk: string; // '高' | '中' | '低'
  };
  remark?: string;
  movementStatus?: string;
  movementHistory?: string[]; // ["20:45 廊下", "18:50 トイレ"]
  vitalChangeRate?: {
    heart: number;
    breathRate: number;
    bloodPressure: number;
    temp: number;
    oxygen: number;
    sleep: number;
    step: number;
  };
  // リストデータから取得する追加情報
  acuteChangeLevel?: string;
  acuteChangeScore?: number;
  locationLevel?: string;
  locationZone?: string;
  sleepLevel?: string;
  sleepScore?: number;
  sleepHours?: string; // 睡眠時間（例: "2.8h"）
}

@Component({
  selector: 'app-patient-detail-popup',
  templateUrl: './patient-detail-popup.component.html',
  styleUrls: ['./patient-detail-popup.component.css']
})
export class PatientDetailPopupComponent implements OnInit, OnDestroy, OnChanges {
  @Input() patientId: number | null = null;
  @Input() patientList: NewPatientInfo[] = [];
  @Input() useTestData: boolean = true; // テストデータを使用するかどうか（デフォルト: true）
  @Output() close = new EventEmitter<void>();

  patientDetail: PatientDetailInfo | null = null;
  loadingDetail = false;
  showPopup = false;
  testDataMap: { [key: string]: PatientDetailInfo } = {}; // テストデータマップ

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    // テストデータを使用する場合、テストデータを読み込む
    if (this.useTestData) {
      this.loadTestData(() => {
        // テストデータ読み込み完了後に患者詳細を読み込む
        if (this.patientId) {
          this.loadPatientDetail(this.patientId);
        }
      });
    } else {
      if (this.patientId) {
        this.loadPatientDetail(this.patientId);
      }
    }
  }

  /**
   * テストデータを読み込む
   */
  loadTestData(callback?: () => void): void {
    this.http.get<{ [key: string]: PatientDetailInfo }>('/assets/test-data/patient-detail-test-data.json')
      .subscribe(
        (data: { [key: string]: PatientDetailInfo }) => {
          this.testDataMap = data;
          if (callback) {
            callback();
          }
        },
        (error: HttpErrorResponse) => {
          // 患者詳細テストデータの読み込みに失敗
          if (callback) {
            callback();
          }
        }
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // patientIdが変更された場合、データを再読み込み
    if (changes['patientId'] && this.patientId) {
      if (this.useTestData && Object.keys(this.testDataMap).length === 0) {
        // テストデータがまだ読み込まれていない場合、先に読み込む
        this.loadTestData(() => {
          this.loadPatientDetail(this.patientId!);
        });
      } else {
        this.loadPatientDetail(this.patientId);
      }
    }
  }

  ngOnDestroy(): void {
    // クリーンアップ処理があればここに記述
  }

  /**
   * 患者詳細を取得
   * APIパス: GET /api/v1/patient/{patientId}
   */
  loadPatientDetail(patientId: number): void {
    this.loadingDetail = true;
    this.showPopup = true;
    
    // テストデータを使用する場合
    if (this.useTestData) {
      // テストデータマップから取得
      const testData = this.testDataMap[patientId.toString()];
      if (testData) {
        // リストから追加情報を取得してマージ
        const patient = this.patientList.find(p => p.patientId === patientId);
        // 睡眠時間を計算（scoreから推定：score 0 = 8h, score 25 = 2h）
        const sleepScore = patient?.sleepRisk?.score || 0;
        const sleepHours = sleepScore === 0 ? '8.0' : (8 - (sleepScore / 25) * 6).toFixed(1);
        
        this.patientDetail = {
          ...testData,
          // リストデータから急変リスク、位置リスク、睡眠リスクの詳細を取得
          acuteChangeLevel: patient?.acuteChangeRisk?.level,
          acuteChangeScore: patient?.acuteChangeRisk?.score,
          locationLevel: patient?.locationRisk?.level,
          locationZone: patient?.locationRisk?.zone || testData.movementStatus,
          sleepLevel: patient?.sleepRisk?.level,
          sleepScore: patient?.sleepRisk?.score,
          sleepHours: sleepHours + 'h',
          // vitalChangeRateをテストデータから取得
          vitalChangeRate: testData.vitalChangeRate || undefined
        };
        this.loadingDetail = false;
        return;
      } else {
        // テストデータが見つからない場合、リストから基本情報を使用
        const patient = this.patientList.find(p => p.patientId === patientId);
        if (patient) {
          this.patientDetail = {
            patientId: patient.patientId,
            patientName: patient.patientName,
            age: patient.age,
            gender: patient.gender,
            ward: patient.ward,
            floor: patient.floor,
            room: patient.room,
            bed: patient.bed || '',
            assessment: patient.assessment ? {
              delirium: patient.assessment.deliriumRisk === '高' ? 'あり' : 'なし',
              fallRisk: patient.assessment.fallRisk
            } : undefined,
            acuteChangeLevel: patient.acuteChangeRisk?.level,
            acuteChangeScore: patient.acuteChangeRisk?.score,
            locationLevel: patient.locationRisk?.level,
            locationZone: patient.locationRisk?.zone,
            sleepLevel: patient.sleepRisk?.level,
            sleepScore: patient.sleepRisk?.score,
            sleepHours: patient.sleepRisk?.score === 0 ? '8.0h' : (8 - ((patient.sleepRisk?.score || 0) / 25) * 6).toFixed(1) + 'h'
          };
        }
        this.loadingDetail = false;
        return;
      }
    }
    
    // 実APIから取得
    this.http.get<any>(`/patient/${patientId}`)
      .subscribe(
        (res: any) => {
          if (res.resultCode === 200 && res.data) {
            // APIドキュメントの構造に基づいてデータをマッピング
            this.patientDetail = {
              patientId: res.data.patientId,
              patientName: res.data.patientName,
              age: res.data.age,
              gender: res.data.gender,
              ward: res.data.ward,
              floor: res.data.floor,
              room: res.data.room || '',
              bed: res.data.bed || '',
              assessment: res.data.assessment || undefined,
              remark: res.data.remark,
              movementStatus: res.data.movementStatus,
              movementHistory: res.data.movementHistory || [],
              vitalChangeRate: res.data.vitalChangeRate || undefined
            };
          }
          this.loadingDetail = false;
        },
        (e: HttpErrorResponse) => {
          // 患者詳細APIリクエストが失敗
          this.loadingDetail = false;
          // エラー時はリストから基本情報を使用
          const patient = this.patientList.find(p => p.patientId === patientId);
          if (patient) {
            this.patientDetail = {
              patientId: patient.patientId,
              patientName: patient.patientName,
              age: patient.age,
              gender: patient.gender,
              ward: patient.ward,
              floor: patient.floor,
              room: patient.room,
              bed: patient.bed || '',
              assessment: patient.assessment ? {
                delirium: patient.assessment.deliriumRisk === '高' ? 'あり' : 'なし',
                fallRisk: patient.assessment.fallRisk
              } : undefined
            };
          }
        }
      );
  }

  /**
   * ポップアップを閉じる
   */
  closePopup(): void {
    this.showPopup = false;
    this.patientDetail = null;
    this.close.emit();
  }

  /**
   * ESCキーでポップアップを閉じる
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.showPopup) {
      this.closePopup();
    }
  }

  /**
   * ポップアップのオーバーレイクリックで閉じる
   */
  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closePopup();
    }
  }

  /**
   * 位置アイコンを取得
   */
  getLocationIcon(location: string): string {
    if (location.includes('廊下')) return 'assets/images/icon/delirium/hallway.svg';
    if (location.includes('トイレ')) return 'assets/images/icon/delirium/toilet.svg';
    if (location.includes('他室')) return 'assets/images/icon/delirium/other_room.svg';
    return 'assets/images/icon/delirium/in_bed.svg';
  }

  /**
   * 位置テキストを取得（移動履歴から）
   */
  getLocationText(historyItem: string): string {
    // "20:45 廊下" から "廊下" を抽出
    const parts = historyItem.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : historyItem;
  }

  /**
   * 時間テキストを取得（移動履歴から）
   */
  getTimeText(historyItem: string): string {
    // "20:45 廊下" から "20:45" を抽出
    const parts = historyItem.split(' ');
    return parts.length > 0 ? parts[0] : '';
  }

  /**
   * バイタル変動率の表示テキストを取得
   */
  getVitalChangeText(value: number | undefined): string {
    if (value === null || value === undefined) return '-';
    const prefix = value > 0 ? '+' : '';
    return prefix + value.toFixed(1) + '%';
  }

  /**
   * バイタル変動率のCSSクラスを取得
   */
  getVitalChangeClass(value: number | undefined): string {
    if (value === null || value === undefined) return 'popup-change-stable';
    if (value > 0) return 'popup-change-up';
    if (value < 0) return 'popup-change-down';
    return 'popup-change-stable';
  }

  /**
   * リスクレベルの背景色クラスを取得
   */
  getRiskBgClass(level: string | undefined): string {
    if (level === '高') return 'popup-bg-high';
    if (level === '中') return 'popup-bg-medium';
    return 'popup-bg-none';
  }

  /**
   * 注意事項のリスクレベルを取得（fallRiskから判断）
   */
  getAssessmentLevel(fallRisk: string | undefined): string {
    if (!fallRisk) return '-';
    return fallRisk; // '高' | '中' | '低'
  }

  /**
   * 年齢の表示テキストを取得
   */
  getAgeText(age: number | undefined): string {
    if (age == null) return '-';
    return age + '歳';
  }

  /**
   * 部屋番号の表示テキストを取得
   */
  getRoomText(room: string | undefined): string {
    if (!room) return '-';
    return room + '号';
  }

  /**
   * せん妄の表示テキストを取得
   */
  getDeliriumText(delirium: string | undefined): string {
    if (!delirium) return '-';
    // APIから返されるのは'あり'/'なし'、または'高'/'中'/'低'
    if (delirium === 'あり' || delirium === '高' || delirium === '中' || delirium === '低') {
      return delirium === 'あり' || delirium === '高' ? 'あり' : delirium;
    }
    return delirium;
  }

  /**
   * 変動率に基づいて形状スタイルを取得
   */
  getShapeStyle(changeValue: number | undefined): any {
    if (changeValue === null || changeValue === undefined) {
      // データなし：白色円形（灰色ボーダー付き）
      return {
        display: 'inline-block',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: '#FFFFFF',
        border: '1px solid #ccc'
      };
    }
    
    const absValue = Math.abs(changeValue);
    if (absValue >= 10) {
      // 危険：赤色正方形
      return {
        display: 'inline-block',
        width: '16px',
        height: '16px',
        backgroundColor: '#F3141B'
      };
    } else if (absValue >= 5) {
      // 注意：黄色三角形（上向き）
      return {
        display: 'inline-block',
        width: '0',
        height: '0',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderBottom: '14px solid #FFF600',
        backgroundColor: 'transparent'
      };
    } else {
      // 正常：白色円形（灰色ボーダー付き）
      return {
        display: 'inline-block',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: '#FFFFFF',
        border: '1px solid #ccc'
      };
    }
  }

  /**
   * 睡眠スコアに基づいて形状スタイルを取得
   */
  getSleepShapeStyle(score: number | undefined): any {
    if (score === null || score === undefined) {
      score = 0;
    }
    
    if (score >= 25) {
      // 覚醒：赤色正方形
      return {
        display: 'inline-block',
        width: '16px',
        height: '16px',
        backgroundColor: '#F3141B'
      };
    } else if (score >= 5) {
      // 断続的睡眠：緑色円形
      return {
        display: 'inline-block',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: '#52c41a'
      };
    } else {
      // 安定睡眠：白色円形（灰色ボーダー付き）
      return {
        display: 'inline-block',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: '#FFFFFF',
        border: '1px solid #ccc'
      };
    }
  }
}

